// engines/lyrics-engine.js — pure state machine.
//
// Direct JS port of `Assets/_App/Engines/LyricsEngine.cs` from the Unity port.
// Stateless O(log n) binary search resolving the active lyric line from a
// playhead time. Safe to call ~10× per second without UI churn — fires
// `lineChanged` only when the active index actually changes.
//
// Architecture rule (verbatim from CLAUDE.md): pure state machine, no DOM
// imports, communicates through CustomEvents on its own EventTarget.

/** @typedef {import("../types").LyricLine} LyricLine */

export class LyricsEngine extends EventTarget {
  constructor() {
    super();
    /** @type {LyricLine[] | null} */ this._lines = null;
    /** @type {number} */ this._lastIndex = -1;
  }

  // ── Load / Clear ───────────────────────────────────────────────────────────

  /**
   * Load a set of lyric lines. Lines must be sorted by timeSec (ascending);
   * the JSON loader guarantees this.
   * @param {LyricLine[]} lines
   */
  loadLyrics(lines) {
    this._lines = lines;
    this._lastIndex = -1;
  }

  /** Clear the current lyric set (e.g. when a new item is selected). */
  clear() {
    this._lines = null;
    this._lastIndex = -1;
  }

  // ── Query ──────────────────────────────────────────────────────────────────

  /** True if lyrics are loaded and have at least one line. */
  get hasLyrics() {
    return Array.isArray(this._lines) && this._lines.length > 0;
  }

  /** Total number of lyric lines. */
  get lineCount() { return this._lines?.length ?? 0; }

  /**
   * Return the index of the lyric line that should be displayed at the given
   * playhead time. Highest index whose timeSec ≤ timeSec; 0 if before the
   * first line; lineCount-1 if past the last. Returns -1 when no lyrics.
   *
   * Fires `lineChanged` (CustomEvent<number>) when the active index changes.
   *
   * @param {number} timeSec
   * @returns {number}
   */
  getCurrentLineIndex(timeSec) {
    if (!this.hasLyrics) return -1;
    const lines = /** @type {LyricLine[]} */ (this._lines);
    let lo = 0, hi = lines.length - 1, result = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (lines[mid].timeSec <= timeSec) { result = mid; lo = mid + 1; }
      else { hi = mid - 1; }
    }
    if (result !== this._lastIndex) {
      this._lastIndex = result;
      this.dispatchEvent(new CustomEvent("lineChanged", { detail: result }));
    }
    return result;
  }

  /**
   * Return the line at the given index, or null if out of range.
   * @param {number} index
   * @returns {LyricLine | null}
   */
  getLine(index) {
    if (!this._lines || index < 0 || index >= this._lines.length) return null;
    return this._lines[index];
  }

  /**
   * Return the line index that will become active *after* the current one,
   * useful for pre-scrolling. -1 if there is no next line.
   * @param {number} timeSec
   * @returns {number}
   */
  getNextLineIndex(timeSec) {
    const cur = this.getCurrentLineIndex(timeSec);
    const next = cur + 1;
    return next < this.lineCount ? next : -1;
  }

  // ── Snapshot ───────────────────────────────────────────────────────────────

  /**
   * Plain-object snapshot for screens to render. Mirrors the snapshot
   * convention from the Unity port; screens read this rather than reaching
   * into engine internals.
   * @returns {{ hasLyrics: boolean, lineCount: number, currentIndex: number, currentLine: LyricLine | null }}
   */
  snapshot() {
    return {
      hasLyrics: this.hasLyrics,
      lineCount: this.lineCount,
      currentIndex: this._lastIndex,
      currentLine: this.getLine(this._lastIndex),
    };
  }
}
